import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApiClient } from '../api/client';
import { Card, CardTitle, CardBody } from '../components/ui/Card';
import { Pagination } from '../components/ui/Pagination';
import { Loading } from '../components/ui/Loading';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import {
  Bell, Plus, Trash2, Edit2, ToggleLeft, ToggleRight,
  AlertTriangle, Info, AlertCircle, CheckCircle2, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { GlobalNotification, NotificationSeverity } from '../types/admin';

const SEVERITY_OPTIONS: NotificationSeverity[] = ['info', 'success', 'warning', 'error'];

const SEVERITY_BADGE: Record<NotificationSeverity, 'info' | 'success' | 'warning' | 'error'> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

const SEVERITY_ICON: Record<NotificationSeverity, React.ElementType> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: AlertCircle,
};

function isExpired(notification: GlobalNotification): boolean {
  return !!notification.expires_at && new Date(notification.expires_at) < new Date();
}

function getStatusBadge(notification: GlobalNotification) {
  if (!notification.is_active) return <Badge variant="default">Inactive</Badge>;
  if (isExpired(notification)) return <Badge variant="default">Expired</Badge>;
  return <Badge variant="success">Active</Badge>;
}

const Notifications: React.FC = () => {
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formSeverity, setFormSeverity] = useState<NotificationSeverity>('info');
  const [formExpiresAt, setFormExpiresAt] = useState('');
  const pageSize = 15;
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', page, pageSize],
    queryFn: () => adminApiClient.getNotifications({ page, page_size: pageSize }),
  });

  const resetForm = () => {
    setFormTitle('');
    setFormMessage('');
    setFormSeverity('info');
    setFormExpiresAt('');
    setEditingId(null);
    setShowForm(false);
  };

  const createMutation = useMutation({
    mutationFn: () =>
      adminApiClient.createNotification({
        title: formTitle,
        message: formMessage,
        severity: formSeverity,
        expires_at: formExpiresAt ? new Date(formExpiresAt).toISOString() : undefined,
      }),
    onSuccess: () => {
      toast.success('Notification created');
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create notification');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      adminApiClient.updateNotification(id, data),
    onSuccess: () => {
      toast.success('Notification updated');
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update notification');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApiClient.deleteNotification(id),
    onSuccess: () => {
      toast.success('Notification deleted');
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete notification');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      adminApiClient.updateNotification(id, { is_active: isActive }),
    onSuccess: (data) => {
      toast.success(`Notification ${data.is_active ? 'activated' : 'deactivated'}`);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to toggle notification');
    },
  });

  const startEdit = (notification: GlobalNotification) => {
    setEditingId(notification.id);
    setFormTitle(notification.title);
    setFormMessage(notification.message);
    setFormSeverity(notification.severity);
    setFormExpiresAt(
      notification.expires_at ? notification.expires_at.slice(0, 16) : ''
    );
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      updateMutation.mutate({
        id: editingId,
        data: {
          title: formTitle,
          message: formMessage,
          severity: formSeverity,
          expires_at: formExpiresAt ? new Date(formExpiresAt).toISOString() : undefined,
        },
      });
    } else {
      createMutation.mutate();
    }
  };

  const handleDelete = (id: string, title: string) => {
    if (window.confirm(`Delete notification "${title}"? This cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Bell className="w-6 h-6 text-primary-400" />
          <h2 className="text-2xl font-bold text-white">Global Notifications</h2>
        </div>
        <Button
          variant={showForm ? 'ghost' : 'primary'}
          size="sm"
          onClick={() => {
            if (showForm) {
              resetForm();
            } else {
              setShowForm(true);
            }
          }}
        >
          {showForm ? (
            <><X className="w-4 h-4 mr-1" /> Cancel</>
          ) : (
            <><Plus className="w-4 h-4 mr-1" /> Create</>
          )}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardTitle>{editingId ? 'Edit Notification' : 'Create Notification'}</CardTitle>
          <CardBody>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
                <input
                  type="text"
                  required
                  maxLength={255}
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Notification title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Message</label>
                <textarea
                  required
                  value={formMessage}
                  onChange={(e) => setFormMessage(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Notification message"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Severity</label>
                  <select
                    value={formSeverity}
                    onChange={(e) => setFormSeverity(e.target.value as NotificationSeverity)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {SEVERITY_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Expires At <span className="text-gray-500">(optional)</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={formExpiresAt}
                    onChange={(e) => setFormExpiresAt(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <Button variant="ghost" size="sm" type="button" onClick={resetForm}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit" loading={isPending} disabled={isPending}>
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card padding="none">
        <div className="p-4 border-b border-gray-700">
          <CardTitle>
            Notifications {data ? `(${data.total})` : ''}
          </CardTitle>
        </div>
        {isLoading ? (
          <Loading />
        ) : data?.data?.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">No notifications yet</p>
            <p className="text-sm mt-1">Create a global notification to broadcast to all users</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-700/50">
              {data?.data?.map((notification) => {
                const SeverityIcon = SEVERITY_ICON[notification.severity];
                return (
                  <div
                    key={notification.id}
                    className="px-4 py-3 flex items-start justify-between gap-4 hover:bg-gray-800/30 transition-colors"
                  >
                    <div className="flex items-start space-x-3 min-w-0 flex-1">
                      <SeverityIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                        notification.severity === 'error' ? 'text-red-400' :
                        notification.severity === 'warning' ? 'text-yellow-400' :
                        notification.severity === 'success' ? 'text-green-400' :
                        'text-blue-400'
                      }`} />
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {notification.title}
                        </p>
                        <p className="text-gray-400 text-xs mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <Badge variant={SEVERITY_BADGE[notification.severity]}>
                            {notification.severity}
                          </Badge>
                          {getStatusBadge(notification)}
                          <span className="text-gray-500 text-xs">
                            by {notification.created_by_admin_email}
                          </span>
                          <span className="text-gray-600 text-xs">
                            {new Date(notification.created_at).toLocaleDateString()}
                          </span>
                          {notification.expires_at && (
                            <span className="text-gray-600 text-xs">
                              expires {new Date(notification.expires_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <button
                        onClick={() => toggleMutation.mutate({
                          id: notification.id,
                          isActive: !notification.is_active,
                        })}
                        disabled={toggleMutation.isPending}
                        className="p-1.5 text-gray-400 hover:text-white rounded transition-colors"
                        title={notification.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {notification.is_active ? (
                          <ToggleRight className="w-5 h-5 text-green-400" />
                        ) : (
                          <ToggleLeft className="w-5 h-5" />
                        )}
                      </button>
                      <button
                        onClick={() => startEdit(notification)}
                        className="p-1.5 text-gray-400 hover:text-white rounded transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(notification.id, notification.title)}
                        disabled={deleteMutation.isPending}
                        className="p-1.5 text-gray-400 hover:text-red-400 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {data && data.total_pages > 1 && (
              <div className="p-3 border-t border-gray-700">
                <Pagination
                  currentPage={page}
                  totalPages={data.total_pages}
                  totalItems={data.total}
                  pageSize={pageSize}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default Notifications;
